/*
  Vidéo de démonstration (section q4-video, v14) : la source MP4 n'est posée qu'à l'approche de l'écran (rien ne se
  télécharge au chargement de la page), lecture sans son en boucle tant que la vidéo est visible, pause dès qu'elle
  sort de l'écran. Pas de lecture automatique si le visiteur demande moins d'animations ou économise ses données :
  le bouton la lance. Source 720p sur mobile, 1080p au-delà.
  v17 : diaporama d'attente (pas encore de vidéo). Les images se succèdent quand la barre de progression de l'image
  courante arrive au bout (fin de l'animation CSS) : mettre l'animation en pause suffit à tout arrêter, sans
  minuterie. Pause au bouton (WCAG 2.2.2), au focus clavier (reprise au bouton seulement, motif « carrousel » de
  l'APG), au survol à la souris, hors de l'écran et quand l'onglet est masqué. Les changements demandés par le
  visiteur sont annoncés aux lecteurs d'écran ; le défilement automatique, non.
  Chargé en « defer » ; chaque vidéo ou diaporama est initialisé une fois, y compris après un rechargement de
  section dans l'éditeur de thème (événement shopify:section:load).
*/
(() => {
  const moinsAnimations = window.matchMedia('(prefers-reduced-motion: reduce)');
  const economie = () => Boolean(navigator.connection && navigator.connection.saveData);

  const initialiser = (cadre) => {
    if (cadre.dataset.q4Pret) return;
    cadre.dataset.q4Pret = '1';
    const video = cadre.querySelector('video');
    const bouton = cadre.querySelector('[data-q4-video-bouton]');
    if (!video || !bouton) return;
    video.muted = true;

    // Pause voulue par le visiteur (bouton), ou lecture automatique non souhaitée
    let pauseVoulue = cadre.dataset.auto !== 'true' || moinsAnimations.matches || economie();
    let chargee = false;

    const charger = () => {
      if (chargee) return;
      chargee = true;
      const petite = window.innerWidth < 750 || economie();
      video.src = (petite && video.dataset.srcSd) || video.dataset.srcHd || video.dataset.srcSd;
    };
    const lire = () => {
      charger();
      const promesse = video.play();
      if (promesse) promesse.catch(() => {});
    };
    const majBouton = () => {
      const enLecture = !video.paused;
      bouton.dataset.etat = enLecture ? 'lecture' : 'pause';
      bouton.setAttribute('aria-label', enLecture ? 'Mettre la vidéo en pause' : 'Lire la vidéo');
    };

    video.addEventListener('play', majBouton);
    video.addEventListener('pause', majBouton);
    video.addEventListener('playing', () => cadre.classList.add('q4-video--lue'), { once: true });
    bouton.addEventListener('click', () => {
      if (video.paused) {
        pauseVoulue = false;
        lire();
      } else {
        pauseVoulue = true;
        video.pause();
      }
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entrees) => {
          entrees.forEach((entree) => {
            if (entree.isIntersecting) {
              if (!pauseVoulue) lire();
            } else if (!video.paused) {
              video.pause();
            }
          });
        },
        { rootMargin: '200px 0px' }
      ).observe(cadre);
    } else if (!pauseVoulue) {
      lire();
    }
    majBouton();
  };

  const initialiserDiaporama = (racine) => {
    if (racine.dataset.q4Pret) return;
    const vues = Array.from(racine.querySelectorAll('[data-q4-vue]'));
    const legendes = Array.from(racine.querySelectorAll('[data-q4-legende]'));
    const reperes = Array.from(racine.querySelectorAll('[data-q4-repere]'));
    const bouton = racine.querySelector('[data-q4-video-bouton]');
    const annonce = racine.querySelector('[data-q4-annonce]');
    const liste = racine.querySelector('.q4-diapo__reperes');
    if (vues.length < 2 || !bouton || !liste) return;
    racine.dataset.q4Pret = '1';

    let courant = 0;
    let pauseVoulue = racine.dataset.auto !== 'true' || moinsAnimations.matches || economie();
    let visible = false;
    let survol = false;

    const majLecture = () => {
      const enLecture = !pauseVoulue && visible && !survol && !document.hidden;
      racine.classList.toggle('est-en-lecture', enLecture);
      bouton.dataset.etat = pauseVoulue ? 'pause' : 'lecture';
      bouton.setAttribute('aria-label', pauseVoulue ? 'Lire le diaporama' : 'Mettre le diaporama en pause');
    };

    // Relance une animation CSS déjà appliquée (retour sur une image juste quittée)
    const relancer = (element) => {
      if (!element) return;
      element.style.animation = 'none';
      void element.offsetWidth;
      element.style.animation = '';
    };

    const aller = (index, parLeVisiteur) => {
      const precedent = courant;
      courant = (index + vues.length) % vues.length;
      vues.forEach((vue, i) => {
        vue.classList.toggle('est-active', i === courant);
        vue.classList.toggle('est-sortante', i === precedent && precedent !== courant);
        if (i === courant) vue.removeAttribute('aria-hidden');
        else vue.setAttribute('aria-hidden', 'true');
      });
      legendes.forEach((legende, i) => {
        legende.classList.toggle('est-active', i === courant);
        if (i === courant) legende.removeAttribute('aria-hidden');
        else legende.setAttribute('aria-hidden', 'true');
      });
      reperes.forEach((repere, i) => {
        repere.classList.toggle('est-vue', i < courant);
        if (i === courant) repere.setAttribute('aria-current', 'true');
        else repere.removeAttribute('aria-current');
      });
      relancer(vues[courant].querySelector('.q4-diapo__image'));
      relancer(reperes[courant] && reperes[courant].querySelector('.q4-diapo__plein'));
      if (parLeVisiteur && annonce && legendes[courant]) {
        annonce.textContent = `Image ${courant + 1} sur ${vues.length} : ${legendes[courant].textContent.replace(/^\d+/, '').trim()}`;
      }
    };

    // Image suivante quand la barre de progression de l'image courante est pleine
    racine.addEventListener('animationend', (evenement) => {
      if (evenement.animationName !== 'q4-diapo-progres') return;
      if (!reperes[courant] || !reperes[courant].contains(evenement.target)) return;
      aller(courant + 1, false);
    });

    reperes.forEach((repere, i) => {
      repere.addEventListener('click', () => aller(i, true));
    });

    bouton.addEventListener('click', () => {
      pauseVoulue = !pauseVoulue;
      majLecture();
    });

    // Focus clavier dans le diaporama : arrêt, reprise seulement au bouton
    racine.addEventListener('focusin', (evenement) => {
      if (!pauseVoulue && evenement.target.matches(':focus-visible')) {
        pauseVoulue = true;
        majLecture();
      }
    });

    racine.addEventListener('pointerenter', (evenement) => {
      if (evenement.pointerType !== 'mouse') return;
      survol = true;
      majLecture();
    });
    racine.addEventListener('pointerleave', (evenement) => {
      if (evenement.pointerType !== 'mouse') return;
      survol = false;
      majLecture();
    });

    document.addEventListener('visibilitychange', majLecture);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entrees) => {
          entrees.forEach((entree) => {
            visible = entree.intersectionRatio >= 0.4;
            majLecture();
          });
        },
        { threshold: [0, 0.4] }
      ).observe(racine);
    } else {
      visible = true;
    }

    // Éditeur de thème : un bloc sélectionné affiche son image, diaporama arrêté
    document.addEventListener('shopify:block:select', (evenement) => {
      const index = vues.indexOf(evenement.target);
      if (index < 0) return;
      pauseVoulue = true;
      aller(index, false);
      majLecture();
    });

    bouton.hidden = false;
    liste.hidden = false;
    racine.classList.add('est-pret');
    majLecture();
  };

  const toutInitialiser = () => {
    document.querySelectorAll('[data-q4-video]').forEach(initialiser);
    document.querySelectorAll('[data-q4-diaporama]').forEach(initialiserDiaporama);
  };
  toutInitialiser();
  document.addEventListener('shopify:section:load', toutInitialiser);
})();
